<template>
    <div class="cv-calendar calendar-month">
        <div class="calendar-options">
            <div class="calendar-heading">
                <h1>Deadlines</h1>
            </div>

            <div class="calendar-month-header">
                <CalendarDateSelector
                    :current-date="today"
                    :selected-date="selectedDate"
                    :calendarListSelected="openCalendarTrigger"
                    @dateSelected="selectDate"
                />
            </div>

            <div class="calendar-list">
                <a href="" @click.prevent="toggleCalendar(true)"
                    ><i
                        class="ah_icon i-calendar"
                        :class="{ 'i-open-calendar': openCalendarTrigger }"
                    ></i
                ></a>
                <a href="" @click.prevent="toggleCalendar(false)"
                    ><i
                        class="ah_icon i-list"
                        :class="{ 'i-open-list': !openCalendarTrigger }"
                    ></i
                ></a>
            </div>
        </div>

        <div
            class="calendar-weekdays"
            :class="{ hidden: !openCalendarTrigger }"
        >
            <CalendarWeekdays />
        </div>

        <ol class="days-grid" :class="{ hidden: !openCalendarTrigger }">
            <CalendarMonthDayItem
                v-for="day in days"
                :key="day.date"
                :day="day"
                :is-today="day.date === today"
                :deadlines="deadlinesDate"
                @deadlineSelected="selectDeadline"
            />
        </ol>
        <div class="list-grid" :class="{ hidden: openCalendarTrigger }">
            <ol>
                <li
                    v-for="(dl, dl_index) in countArrUniqueDeadlines"
                    :class="{
                        selected_deadline:
                            selectDeadlineDate == dl.calendarDate,
                    }"
                >
                    <strong>{{ dl.formatDate }}</strong>
                    <span class="event-count">{{
                        deadlineEvents[dl.date].length
                    }}</span>
                    <ul class="ul-events">
                        <li v-for="event in deadlineEvents[dl.date]">
                            {{ event.title }}
                        </li>
                    </ul>
                </li>
            </ol>
            <span
                class="view-all"
                v-if="limit == 2"
                @click="toggleViewAllDeadlines"
                >View all ({{ countUniqueDeadlines }})</span
            >
            <span class="view-all" @click="limit = 2" v-else>Show less</span>
            <span class="save-calendar" @click="saveCalendar"
                ><i class="ah_icon i-open-calendar"></i> Save to my
                calendar</span
            >
        </div>
    </div>
</template>

<script>
import API from "../API";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import CalendarMonthDayItem from "./CalendarMonthDayItem";
import CalendarDateIndicator from "./CalendarDateIndicator";
import CalendarDateSelector from "./CalendarDateSelector";
import CalendarWeekdays from "./CalendarWeekdays";

dayjs.extend(weekday);
dayjs.extend(weekOfYear);

export default {
    name: "CalendarMonth",

    components: {
        CalendarMonthDayItem,
        CalendarDateIndicator,
        CalendarDateSelector,
        CalendarWeekdays,
    },

    data() {
        return {
            selectedDate: dayjs(),
            selectDeadlineDate: "",
            openCalendarTrigger: true,
            deadlines: [],
            deadlinesDate: [],
            deadlinesDateData: [],
            deadlineEvents: [],
            limit: 2,
        };
    },

    created() {
        this.fetchData();
    },

    mounted() {
        //
    },

    updated() {
        //
    },

    computed: {
        days() {
            return [
                ...this.previousMonthDays,
                ...this.currentMonthDays,
                ...this.nextMonthDays,
            ];
        },

        today() {
            return dayjs().format("YYYY-MM-DD");
        },

        month() {
            return Number(this.selectedDate.format("M"));
        },

        year() {
            return Number(this.selectedDate.format("YYYY"));
        },

        numberOfDaysInMonth() {
            return dayjs(this.selectedDate).daysInMonth();
        },

        currentMonthDays() {
            return [...Array(this.numberOfDaysInMonth)].map((day, index) => {
                return {
                    date: dayjs(
                        `${this.year}-${this.month}-${index + 1}`
                    ).format("YYYY-MM-DD"),
                    isCurrentMonth: true,
                };
            });
        },

        previousMonthDays() {
            const firstDayOfTheMonthWeekday = this.getWeekday(
                this.currentMonthDays[0].date
            );
            const previousMonth = dayjs(
                `${this.year}-${this.month}-01`
            ).subtract(1, "month");

            // Cover first day of the month being sunday (firstDayOfTheMonthWeekday === 0)
            const visibleNumberOfDaysFromPreviousMonth =
                firstDayOfTheMonthWeekday ? firstDayOfTheMonthWeekday - 1 : 6;

            const previousMonthLastMondayDayOfMonth = dayjs(
                this.currentMonthDays[0].date
            )
                .subtract(visibleNumberOfDaysFromPreviousMonth, "day")
                .date();

            return [...Array(visibleNumberOfDaysFromPreviousMonth)].map(
                (day, index) => {
                    return {
                        date: dayjs(
                            `${previousMonth.year()}-${
                                previousMonth.month() + 1
                            }-${previousMonthLastMondayDayOfMonth + index}`
                        ).format("YYYY-MM-DD"),
                        isCurrentMonth: false,
                    };
                }
            );
        },

        nextMonthDays() {
            const lastDayOfTheMonthWeekday = this.getWeekday(
                `${this.year}-${this.month}-${this.currentMonthDays.length}`
            );

            const nextMonth = dayjs(`${this.year}-${this.month}-01`).add(
                1,
                "month"
            );

            const visibleNumberOfDaysFromNextMonth = lastDayOfTheMonthWeekday
                ? 7 - lastDayOfTheMonthWeekday
                : lastDayOfTheMonthWeekday;

            return [...Array(visibleNumberOfDaysFromNextMonth)].map(
                (day, index) => {
                    return {
                        date: dayjs(
                            `${nextMonth.year()}-${nextMonth.month() + 1}-${
                                index + 1
                            }`
                        ).format("YYYY-MM-DD"),
                        isCurrentMonth: false,
                    };
                }
            );
        },

        uniqueDeadlines() {
            return this.deadlinesDateData.reduce((seed, current) => {
                return Object.assign(seed, {
                    [current.date]: current,
                });
            }, {});
        },

        countArrUniqueDeadlines() {
            const arrUniqueDeadlines = $.map(
                this.uniqueDeadlines,
                function (value, index) {
                    return [value];
                }
            );
            return this.limit
                ? arrUniqueDeadlines.slice(0, this.limit)
                : arrUniqueDeadlines;
        },

        countUniqueDeadlines() {
            return Object.keys(this.uniqueDeadlines).length;
        },
    },

    methods: {
        getWeekday(date) {
            return dayjs(date).weekday();
        },

        selectDate(newSelectedDate) {
            this.selectedDate = newSelectedDate;
        },

        selectDeadline(selectedDeadline) {
            this.openCalendarTrigger = false;
            this.selectDeadlineDate = selectedDeadline.date;
            this.limit = null;
        },

        toggleCalendar(state) {
            if (state) {
                this.openCalendarTrigger = true;
            } else {
                this.openCalendarTrigger = false;
                this.limit = 2;
            }
        },

        fetchData() {
            let that = this;
            let deadlinesFormat = {};
            API.get("deadlines").then((response) => {
                $.each(response.data, function (key, value) {
                    let deadlineDate = dayjs(value.deadline).format("YYYYMMDD");
                    let calendarDate = dayjs(value.deadline).format(
                        "YYYY-MM-DD"
                    );
                    value.formatDate = dayjs(value.deadline).format(
                        "dddd, DD / MM / YYYY"
                    );
                    that.deadlinesDate.push(calendarDate);
                    that.deadlinesDateData.push({
                        calendarDate: calendarDate,
                        date: deadlineDate,
                        formatDate: value.formatDate,
                    });
                    if (!(deadlineDate in deadlinesFormat)) {
                        deadlinesFormat[deadlineDate] = [];
                    }
                    deadlinesFormat[deadlineDate].push({
                        title: value.title,
                    });
                    that.deadlines.push(value);
                });
            });

            this.deadlineEvents = deadlinesFormat;
        },

        toggleViewAllDeadlines() {
            if (this.limit == 2) {
                this.limit = null;
            } else if (this.limit == null) {
                this.limit = 2;
            }

            return this.limit;
        },

        saveCalendar() {
            var href = ["BEGIN:VCALENDAR", "VERSION:2.0"];
            this.deadlines.forEach(function (value) {
                href.push("BEGIN:VEVENT");
                href.push(
                    "DTSTART:" + (dayjs(value.date).format("YYYYMMDD") || "")
                );
                href.push(
                    "DTEND:" + (dayjs(value.date).format("YYYYMMDD") || "")
                );
                href.push("SUMMARY:" + (value.title || ""));
                href.push("DESCRIPTION:" + (value.description || ""));
                href.push("LOCATION:" + ("" || ""));
                href.push(
                    "URL:" +
                        (window.location.origin + window.location.pathname) ||
                        ""
                );
                href.push("END:VEVENT");
            });
            href.push("END:VCALENDAR");
            href = encodeURI(
                "data:text/calendar;charset=utf8," + href.join("\n")
            );
            this._save = function (fileURL) {
                if (!window.ActiveXObject) {
                    var save = document.createElement("a");
                    save.href = fileURL;
                    save.target = "_blank";
                    save.download = "ASIFA Deadlines Calendar";
                    var evt = new MouseEvent("click", {
                        view: window,
                        bubbles: true,
                        cancelable: false,
                    });
                    save.dispatchEvent(evt)(
                        window.URL || window.webkitURL
                    ).revokeObjectURL(save.href);
                } else if (!!window.ActiveXObject && document.execCommand) {
                    var _window = window.open(fileURL, "_blank");
                    _window.document.close();
                    _window.document.execCommand(
                        "SaveAs",
                        true,
                        this.fileName || fileURL
                    );
                    _window.close();
                }
            };
            try {
                this._save(href);
            } catch (e) {
                console.log(e);
            }
        },
    },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/CalendarMonth.vue